let API_BASE = '/api'
if (import.meta.env.VITE_API_URL) {
    API_BASE = import.meta.env.VITE_API_URL
}
else if (import.meta.env.DEV) {
    API_BASE = 'https://api.getmypaystub.localhost/api'
}

import store from '@/store'
import bus from '@/bus'
import * as nativeBridge from '@/native-bridge'
import { noteWrite } from '@/client-freshness'
import 'whatwg-fetch'

const handleError = (err) => {
    if (err === null || typeof err !== 'object') {
        err = {}
    }
    // Only inject the generic transport-failure message for genuine transport
    // errors: a fetch that never received a response (connection dropped, TLS
    // failure, tab backgrounded mid-flight) rejects with a raw TypeError that
    // carries no __status__. Errors already normalized by handleResponse carry a
    // __status__ and their own messaging — including the intentionally-silent
    // 401 (__all__: [], __status__: 401, __silent__: true) used for cold visits
    // with stale persisted auth. Overwriting that empty __all__ here would turn
    // the silent 401 into a spurious "problem communicating with the server"
    // toast on first load (e.g. after an overnight logout).
    if (err.__status__ === undefined && (!err.__all__ || !err.__all__.length)) {
        err.__all__ = ['There was a problem communicating with the server.']
    }
    throw err
}

// Pre-auth login routes (/login, /login/complete, ...): a 401 there is a
// login-flow answer with its own message, not an expired session.
const isLoginPath = (url) => /^\/login(\/|\?|$)/.test(url || '')

// Reasons the app shows on its sign-in screen (see native-bridge.js)
const NATIVE_LOGOUT_REASONS = ['mfa_required', 'mfa_enrollment_required']

// A 401 outside the login flow: the session is over.
const sessionEnded = () => {
    // Distinguish a genuine mid-session expiry from a cold visit carrying
    // stale persisted auth state. session_validated is true only once a real
    // authenticated response landed this page load (see store.js); it resets
    // on reload, so a cold visit always sees it false even though localStorage
    // still has a (now-dead) user/session.
    const wasActive = store.state.session_validated
    store.dispatch('LOGOUT', true)
    if (wasActive) {
        bus.loggedOut()       // active session expired -> "you've been signed out" modal
    }
    else {
        bus.sessionInvalid()  // cold visit / stale state -> silently go to login
    }
    // Reject (rather than resolve undefined) so callers' .then bodies don't
    // run against an undefined response — which either throws a raw
    // TypeError or, worse, silently treats the 401 as success. __all__ is left
    // empty and flagged so callers' .catch blocks don't surface a toast: a 401
    // has exactly one UX — the modal (active expiry) or the login redirect
    // (cold visit). The rejection just lets each caller's .catch unwind cleanly.
    return Promise.reject({__all__: [], __status__: 401, __silent__: true})
}

const handleResponse = (response, url = '') => {
    // if we got an app version, save it
    let appVersion = response.headers.get('X-GetMyPayStub-App-Version')
    if (appVersion) {
        store.dispatch('SET_APP_VERSION', appVersion)
    }

    if (response.status == 401 && !isLoginPath(url)) {
        return Promise.resolve()
            .then(() => response.json())
            .catch(() => null)
            .then((body) => {
                const reason = body && body.reason
                // Inside the app, an MFA refusal's reason goes to the host first, so
                // its sign-in screen can say why (app 2.1+; older hosts ignore it).
                if (nativeBridge.isNativeHost()) {
                    if (NATIVE_LOGOUT_REASONS.includes(reason)) {
                        nativeBridge.post({ type: 'logout', reason })
                    }
                    return sessionEnded()
                }
                // On the web, a setup-only session stays signed in on the setup page
                if (reason === 'mfa_enrollment_required') {
                    bus.mfaSetupRequired()
                    return Promise.reject({__all__: [], __status__: 401, __silent__: true})
                }
                return sessionEnded()
            })
    }

    // if we got an expiration time, save it
    let session_expiry = response.headers.get('X-GetMyPayStub-Session-Expiry')
    if (session_expiry) {
        store.dispatch('SET_SESSION_EXPIRY', session_expiry)
    }

    if (response.status > 399) {
        return response.json().catch(() => {
            switch (response.status) {
                case 404:
                    throw {'__all__': ['A programming error has occurred.'], '__status__': 404}
                default:
                    throw {'__all__': ['An error has occurred.'], '__status__': response.status}
            }
        }).then(err => {
            err.__status__ = response.status
            if (!err.__all__ || !err.__all__.length) {
                err.__all__ = ['An error has occurred.']
            }
            throw err
        })
    }

    if (response.headers.get('content-type') == 'application/json') {
        return response.json()
    }
    if (response.headers.get('content-type') == 'application/pdf') {
        return response.arrayBuffer()
    }
    if (response.headers.get('content-type') == 'application/zip') {
        return response.arrayBuffer()
    }
    return response.text()
}

const getHeaders = () => {
    return new Headers({
        'Content-Type': 'application/json',
        'X-CSRFToken': store.state.session ? store.state.session.csrf_token : '',
    })
}

const put = (url, data) => {
    data['csrfmiddlewaretoken'] = store.state.session ? store.state.session.csrf_token : ''
    return fetch(`${API_BASE}${url}`, {
        method: 'PUT',
        body: JSON.stringify(data),
        headers: getHeaders(),
        credentials: 'include',
    }).then(r => handleResponse(r, url)).catch(handleError).finally(() => noteWrite(url))
}

const patch = (url, data) => {
    data['csrfmiddlewaretoken'] = store.state.session ? store.state.session.csrf_token : ''
    return fetch(`${API_BASE}${url}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: getHeaders(),
        credentials: 'include',
    }).then(r => handleResponse(r, url)).catch(handleError).finally(() => noteWrite(url))
}

const post = (url, data) => {
    data['csrfmiddlewaretoken'] = store.state.session ? store.state.session.csrf_token : ''
    return fetch(`${API_BASE}${url}`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: getHeaders(),
        credentials: 'include',
    }).then(r => handleResponse(r, url)).catch(handleError).finally(() => noteWrite(url))
}

const get = (url, ignore_for_session_expiry = false, prefix_api_base = true) => {
    // for gets caused by async updates, we send a header
    // to let the server know *not* to extend the session
    // (GET requests only)
    const headers = getHeaders()
    if (ignore_for_session_expiry) {
        headers.append('X-GetMyPayStub-Session-Ignore', '1')
    }
    const relUrl = prefix_api_base ? url : ''
    if (prefix_api_base) {
        url = `${API_BASE}${url}`
    }
    return fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
    }).then(r => handleResponse(r, relUrl)).catch(handleError)
}

// Retry a GET when the request fails at the transport layer (connection
// dropped, TLS failure, tab backgrounded mid-flight — Safari reports these as
// "TypeError: Load failed"). Such failures never receive a response, so the
// normalized error carries no __status__; real HTTP errors always set one and
// are NOT retried. GETs are idempotent, so replaying a dropped request is safe.
// Use for fetches the app can't proceed without, where a transient blip would
// otherwise leave it wedged (e.g. security roles, which gate the whole UI).
const getWithRetry = (url, {ignoreForSessionExpiry = false, prefixApiBase = true, retries = 3, backoffMs = 500} = {}) => {
    const attempt = (n) => get(url, ignoreForSessionExpiry, prefixApiBase).catch((err) => {
        const isTransportError = !err || err.__status__ === undefined
        if (isTransportError && n < retries) {
            const delay = backoffMs * 2 ** n
            return new Promise(resolve => setTimeout(resolve, delay)).then(() => attempt(n + 1))
        }
        throw err
    })
    return attempt(0)
}

const del = (url) => {
    return fetch(`${API_BASE}${url}`, {
        method: 'DELETE',
        headers: getHeaders(),
        credentials: 'include',
    }).then(r => handleResponse(r, url)).catch(handleError).finally(() => noteWrite(url))
}

const upload = (url, file, {formData, fieldName, noCors}) => {
    const headers = new Headers({
        'X-CSRFToken': store.state.session.csrf_token,
    })

    const data = new FormData()

    // make sure this exists as a dict since it's optional
    formData = formData || {}

    Object.keys(formData).forEach(field => {
        data.append(field, formData[field])
    })
    data.append(fieldName || 'file', file)

    if (url.indexOf('https://') == -1 && url.indexOf('http://') == -1) {
        url = `${API_BASE}${url}`
    }

    let upload_args = {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: data,
    }

    // no-CORS mode only for S3 uploads
    if (noCors) {
        upload_args['mode'] = 'no-cors'
    }

    return fetch(url, upload_args).then(handleResponse).catch(handleError).finally(() => noteWrite(url))
}

const uploadMultiple = (url, fileData, {formData}) => {
    const headers = new Headers({
        'X-CSRFToken': store.state.session.csrf_token,
    })

    const data = new FormData()

    fileData = fileData || {}
    Object.keys(fileData).forEach(field => {
        data.append(field, fileData[field])
    })

    formData = formData || {}
    Object.keys(formData).forEach(field => {
        data.append(field, formData[field])
    })

    return fetch(`${API_BASE}${url}`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: data,
    }).then(handleResponse).catch(handleError).finally(() => noteWrite(url))
}

const redirect = (url) => {
    window.location.href = `${API_BASE}${url}`
}

export default {
    get,
    getWithRetry,
    post,
    put,
    patch,
    del,
    upload,
    uploadMultiple,
    redirect,
    API_BASE,
}
